import { httpHeaders } from '@monorepo/helpers';
import { localStorageHelper } from '../helpers/localStorageHelper';

type SseListener = (event: MessageEvent<string>) => void;

export class SseEventSource {
    private readonly listeners = new Map<string, Set<SseListener>>();
    private controller: AbortController | null = null;
    private connectionId = 0;
    private closed = false;
    //
    constructor(private readonly url: string) {
        this.start();
    }
    addEventListener = (type: string, listener: SseListener): void => {
        const listeners = this.listeners.get(type) ?? new Set<SseListener>();
        listeners.add(listener);
        this.listeners.set(type, listeners);
    }
    removeEventListener = (type: string, listener: SseListener): void => {
        this.listeners.get(type)?.delete(listener);
    }
    close = (): void => {
        this.closed = true;
        this.connectionId += 1;
        this.controller?.abort();
        this.controller = null;
    }
    reconnect = (): void => {
        this.controller?.abort();
        this.closed = false;
        this.start();
    }
    private start = (): void => {
        const connectionId = ++this.connectionId;
        void this.connect(connectionId);
    }
    private connect = async (connectionId: number): Promise<void> => {
        while (!this.closed && connectionId === this.connectionId) {
            const token = localStorageHelper.GetAuthToken();
            const controller = new AbortController();
            this.controller = controller;
            try {
                const headers: Record<string, string> = {};
                if (token) {
                    headers[httpHeaders.authToken] = token;
                }
                const response = await fetch(this.url, {
                    headers,
                    signal: controller.signal
                });
                const renewedToken = response.headers.get(httpHeaders.authToken);
                if (renewedToken) {
                    localStorageHelper.SetAuthToken(renewedToken);
                }
                if (response.ok && response.body) {
                    await this.readEvents(response.body, controller.signal, connectionId);
                } else {
                    await this.delay(3000);
                }
            } catch (error) {
                if (!controller.signal.aborted) {
                    console.error('Error conectando con SSE:', error);
                    await this.delay(3000);
                }
            } finally {
                if (this.controller === controller) {
                    this.controller = null;
                }
            }
        }
    }
    private readEvents = async (
        body: ReadableStream<Uint8Array>,
        signal: AbortSignal,
        connectionId: number
    ): Promise<void> => {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let eventName = 'message';
        let eventData: string[] = [];

        const processLine = (sourceLine: string): void => {
            const line = sourceLine.endsWith('\r') ? sourceLine.slice(0, -1) : sourceLine;
            if (!line) {
                if (eventData.length) {
                    this.dispatch(eventName, eventData.join('\n'));
                }
                eventName = 'message';
                eventData = [];
                return;
            }
            if (line.startsWith(':')) {
                return;
            }
            const separator = line.indexOf(':');
            const field = separator < 0 ? line : line.slice(0, separator);
            const value = separator < 0 ? '' : line.slice(separator + 1).replace(/^ /, '');
            if (field === 'event') {
                eventName = value || 'message';
            } else if (field === 'data') {
                eventData.push(value);
            }
        };

        while (!signal.aborted && connectionId === this.connectionId) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            lines.forEach(processLine);
        }
        buffer += decoder.decode();
        if (buffer) {
            processLine(buffer);
        }
        processLine('');
    }
    private dispatch = (type: string, data: string): void => {
        const event = new MessageEvent<string>(type, { data });
        this.listeners.get(type)?.forEach(listener => listener(event));
    }
    private delay = async (milliseconds: number): Promise<void> =>
        new Promise(resolve => window.setTimeout(resolve, milliseconds));
}
